import { client } from "@/hono";
import { Button, ButtonGroup } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { NumberInput, TextInput } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { responseHandler } from "@msuite/ui/utils";
import { DEFAULT_AUTOMATIC_BREAK_BUFFER_MINUTES } from "@msuite/utils/attendances";
import {
	patchOrganizationSettingsSchema,
	type PatchOrganizationSettingsSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";

export function OrganizationSettingsForm() {
	/** Hooks */
	const methods = useForm({
		resolver: resolver(patchOrganizationSettingsSchema),
		defaultValues: async () => {
			const response = await client.organizations.settings.$get();
			const data = await responseHandler(response);
			return {
				partnerReportsEmail: data.organizationSettings?.partnerReportsEmail,
				automaticBreakBufferMinutes:
					data.organizationSettings?.automaticBreakBufferMinutes ??
					DEFAULT_AUTOMATIC_BREAK_BUFFER_MINUTES,
			} as PatchOrganizationSettingsSchema;
		},
	});
	const { t } = useTranslation();

	/** Functions */
	async function handleOnSubmit(json: PatchOrganizationSettingsSchema) {
		const response = await client.organizations.settings.$patch({ json });
		await responseHandler(response);
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentCard
				title={t("general.advanced")}
				icon={icon("Cog")}
				as="form"
				contentClassName="v-stack gap-4"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<TextInput
					control={methods.control}
					name="partnerReportsEmail"
					label={t("partners.partner_reports_email")}
					placeholder={t("placeholders.email")}
					isRequired
				/>
				<NumberInput
					control={methods.control}
					name="automaticBreakBufferMinutes"
					label={t("organization.automatic_break_buffer")}
					helperText={t("organization.automatic_break_buffer_description")}
					min={0}
					max={45}
					isRequired
				/>
				<Divider variant="content-card" />
				<ButtonGroup className="justify-end">
					<Button
						type="submit"
						variant="primary"
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.submit")}
					</Button>
				</ButtonGroup>
			</ContentCard>
		</FormProvider>
	);
}
